import { useEffect, useState } from 'react';
import { X, UserMinus, RotateCcw, Crown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';

interface Denied {
  id: string;
  user_id: string;
  user: { full_name: string; email: string; display_name: string | null };
}

interface Owner {
  id: string;
  full_name: string;
  email: string;
  display_name: string | null;
}

interface Props {
  projectId: string;
  ownerId: string;
  onClose: () => void;
}

export default function ShareProjectModal({ projectId, ownerId, onClose }: Props) {
  const { user, isPlatformOwner } = useAuthStore();
  const [denied, setDenied] = useState<Denied[]>([]);
  const [owner, setOwner] = useState<Owner | null>(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOwner = user?.id === ownerId;
  const canManage = isOwner || isPlatformOwner();

  const load = async () => {
    const { data: ownerRow } = await supabase
      .from('tb_users')
      .select('id, full_name, email, display_name')
      .eq('id', ownerId)
      .maybeSingle();
    if (ownerRow) setOwner(ownerRow as Owner);

    const { data } = await supabase
      .from('cs_project_denied_users')
      .select('id, user_id, user:tb_users(full_name, email, display_name)')
      .eq('project_id', projectId);
    setDenied(((data || []) as unknown) as Denied[]);
  };

  useEffect(() => {
    load();
  }, [projectId]);

  const block = async () => {
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setLoading(true);
    const { data: target } = await supabase
      .from('tb_users')
      .select('id')
      .ilike('email', trimmed)
      .maybeSingle();
    if (!target) {
      setError(`No user found with email ${trimmed}`);
      setLoading(false);
      return;
    }
    if ((target as { id: string }).id === ownerId) {
      setError('You cannot block the project owner');
      setLoading(false);
      return;
    }
    const { error: insErr } = await supabase
      .from('cs_project_denied_users')
      .insert({
        project_id: projectId,
        user_id: (target as { id: string }).id,
        blocked_by: user?.id,
      });
    if (insErr) {
      if (insErr.code === '23505') setError('That user is already blocked');
      else setError(insErr.message);
    } else {
      setEmail('');
      await load();
    }
    setLoading(false);
  };

  const unblock = async (id: string) => {
    await supabase.from('cs_project_denied_users').delete().eq('id', id);
    await load();
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-[#151518] border border-white/[0.08] rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 className="text-sm font-semibold text-zinc-100">Manage access</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Every designer can edit this project unless you block them below.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.06]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {owner && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/[0.04]">
              <div className="w-8 h-8 rounded-full bg-amber-500/15 flex items-center justify-center">
                <Crown size={14} className="text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-zinc-200 truncate">
                  {owner.display_name || owner.full_name}
                </div>
                <div className="text-[11px] text-zinc-500 truncate">{owner.email}</div>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-amber-400 font-medium">
                Owner
              </span>
            </div>
          )}

          {canManage && (
            <div className="space-y-2">
              <label className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider">
                Block a designer by email
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && block()}
                  placeholder="designer@company.com"
                  className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-cyan-500/40"
                />
                <button
                  onClick={block}
                  disabled={loading}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs font-medium disabled:opacity-50"
                >
                  <UserMinus size={13} /> Block
                </button>
              </div>
              {error && <div className="text-[11px] text-red-400">{error}</div>}
            </div>
          )}

          <div className="space-y-1.5">
            <div className="text-[11px] font-medium text-zinc-400 uppercase tracking-wider">
              Blocked ({denied.length})
            </div>
            {denied.length === 0 ? (
              <div className="text-xs text-zinc-500 p-3 rounded bg-white/[0.02] border border-white/[0.04]">
                No one is blocked. All designers can edit this project.
              </div>
            ) : (
              <div className="space-y-1.5">
                {denied.map((d) => (
                  <div
                    key={d.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.04]"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-zinc-200 truncate">
                        {d.user.display_name || d.user.full_name}
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate">{d.user.email}</div>
                    </div>
                    {canManage && (
                      <button
                        onClick={() => unblock(d.id)}
                        className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-zinc-300 hover:text-emerald-300 hover:bg-emerald-500/10"
                        title="Unblock"
                      >
                        <RotateCcw size={12} /> Unblock
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
